import { BotListData } from '../../modules/builder/typings';
import { ChannelPartnerDetails } from '../../modules/builder/typings/api';
import {
  AnalyticsEventNames,
  AnalyticsEventOptions,
} from '../../shared/typings/analytics';
import { setUserChannelData } from '../../shared/utils/cpDetails';
import BasePlatform from './base';

enum WebMessageTypes {
  LOGIN = 'LOGIN',
  READ_ORIGIN = 'READ_ORIGIN',
  CHANGE_CHANNEL_PARTNER = 'CHANGE_CHANNEL_PARTNER',
}

type WebLoginResult = {
  type: WebMessageTypes.LOGIN;
  data: {
    token: string;
  };
};
type WebOriginResults = {
  type: WebMessageTypes.READ_ORIGIN;
  data: {
    origin: string;
  };
};
type WebChangeChannelPartnerResults = {
  type: WebMessageTypes.CHANGE_CHANNEL_PARTNER;
  data: ChannelPartnerDetails;
};

type WebResult =
  | WebLoginResult
  | WebOriginResults
  | WebChangeChannelPartnerResults;

type WebLoginRequestCache = {
  login?: {
    cachedPromiseResolve: (value: string | PromiseLike<string>) => void;
  };
};

export enum UpgradeModals {
  AI_BOT = 'AI_BOT',
  FACEBOOK_CAPI = 'FACEBOOK_CAPI',
  SLA = 'SLA',
  ENTERPRISE_ANALYTICS = 'ENTERPRISE_ANALYTICS',
}

export type UpgradeModalType =
  | { name: UpgradeModals.AI_BOT; step?: number }
  | { name: UpgradeModals.FACEBOOK_CAPI }
  | { name: UpgradeModals.SLA }
  | { name: UpgradeModals.ENTERPRISE_ANALYTICS };

export default class Web extends BasePlatform {
  cachedPromise: WebLoginRequestCache;

  constructor() {
    super();
    this.cachedPromise = {};
  }

  isCurrentPlatform(): boolean {
    const currentPlatform = typeof window !== 'undefined';
    if (currentPlatform) {
      this.listenForMessageFromWeb();
    }
    return currentPlatform;
  }

  private listenForMessageFromWeb() {
    window.onmessage = ({ data }: { data: any }) => {
      if (typeof data !== 'string') {
        return;
      }

      try {
        this.handleMessageFromWeb(JSON.parse(data));
      } catch {
        // Maybe data was not valid JSON, or event postMessage was called from some 3rd party script
      }
    };
  }

  private handleMessageFromWeb({ type, data }: WebResult) {
    switch (type) {
      case WebMessageTypes.LOGIN:
        this.cachedPromise.login?.cachedPromiseResolve(data.token);
        delete this.cachedPromise.login;
        break;
      case WebMessageTypes.READ_ORIGIN: {
        localStorage?.setItem('parent-origin', data.origin);
        break;
      }
      case WebMessageTypes.CHANGE_CHANNEL_PARTNER: {
        setUserChannelData(data);
        break;
      }
      default:
    }
  }

  /**
   * Single exit point for every message sent to the host frame.
   *
   * The '*' target origin is intentional: this app runs as an iframe inside the
   * DoubleTick desktop app and the host origin varies by environment. It is only
   * learnt at runtime, from the READ_ORIGIN message handled above, so it cannot be
   * pinned at the call site. Centralised so that decision is stated once.
   */
  private postToParent(message: unknown): void {
    window?.parent?.postMessage?.(message, '*', []);
  }

  async getToken(): Promise<string> {
    return new Promise((resolve) => {
      this.cachedPromise.login = {
        cachedPromiseResolve: resolve,
      };

      this.postToParent({ type: 'ASK_TOKEN' });
    });
  }

  openUpgradeModal(data: UpgradeModalType): void {
    this.postToParent({ type: 'OPEN_UPGRADE_MODAL', data });
  }

  getOrigin() {
    this.postToParent({ type: 'ASK_ORIGIN' });
  }

  getVersion(): number {
    return 1;
  }

  goBack(): void {
    this.postToParent({ type: 'GO_BACK' });
  }

  logout(): void {
    // no-op on Web: the parent frame owns the session
  }

  finishActivity(): void {
    this.postToParent({ type: 'CLOSE_IFRAME' });
  }

  redirect(url: string): void {
    this.postToParent({ type: 'REDIRECT', data: { url } });
  }

  dangerouslyRedirectWithReplace(url: string): void {
    this.postToParent({
      type: 'DANGEROUSLY_REDIRECT_WITH_REPLACE',
      data: { url },
    });
  }

  hideModalHeader(title: string): void {
    this.postToParent({ type: 'HIDE_MODAL_HEADER', title });
  }

  showModalHeader(): void {
    this.postToParent({ type: 'SHOW_MODAL_HEADER' });
  }

  addNewBot(data: BotListData): void {
    this.postToParent({ type: 'ADD_BOT', data });
  }

  editBot(data: Partial<BotListData>): void {
    this.postToParent({ type: 'EDIT_BOT', data });
  }

  trackEvent(
    eventName: AnalyticsEventNames,
    properties?: AnalyticsEventOptions | undefined
  ): void {
    this.postToParent({ type: 'TRACK_EVENT', data: { eventName, properties } });
  }
}
